class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // Whenever the drawer becomes visible (by theme code OR by an app), re-sync it with the real cart
    this._wasActive = this.classList.contains('active');
    new MutationObserver(() => {
      const isActive = this.classList.contains('active');
      if (isActive && !this._wasActive) this.refresh();
      this._wasActive = isActive;
    }).observe(this, { attributes: true, attributeFilter: ['class'] });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) { this.setActiveElement(triggeredBy); this.refresh(); }
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  // Re-sync the drawer with the real cart (fixes stale "cart is empty" state when
  // items are added by apps/bundle widgets that bypass product-form.js)
  async refresh() {
    if (this._refreshing) return;
    this._refreshing = true;
    try {
      const [sections, cart] = await Promise.all([
        fetch('/?sections=cart-drawer,cart-icon-bubble&t=' + Date.now()).then((r) => r.json()),
        fetch('/cart.js?t=' + Date.now()).then((r) => r.json())
      ]);
      this.classList.toggle('is-empty', cart.item_count === 0);
      const drawerEl = document.querySelector('#CartDrawer');
      if (drawerEl && sections['cart-drawer']) {
        drawerEl.innerHTML = this.getSectionInnerHTML(sections['cart-drawer'], '#CartDrawer');
        const overlay = this.querySelector('#CartDrawer-Overlay');
        if (overlay) overlay.addEventListener('click', this.close.bind(this));
      }
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && sections['cart-icon-bubble']) {
        bubble.innerHTML = this.getSectionInnerHTML(sections['cart-icon-bubble'], '.shopify-section');
      }
    } catch (e) {
      console.error('cart-drawer refresh failed', e);
    } finally {
      this._refreshing = false;
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Keep the drawer in sync (and open it) after cart adds made through window.fetch
(function () {
  if (window.__cartDrawerSync) return;
  window.__cartDrawerSync = true;
  const origFetch = window.fetch;
  let timer;
  window.fetch = function (...args) {
    const p = origFetch.apply(this, args);
    try {
      const req = args[0];
      const url = typeof req === 'string' ? req : (req && req.url) || '';
      const method = ((args[1] && args[1].method) || (req && req.method) || 'GET').toUpperCase();
      const m = method === 'POST' && url.match(/\/cart\/(add|update|clear)(\.js)?(\?|$)/);
      if (m) {
        const isAdd = m[1] === 'add';
        p.then((res) => {
          if (isAdd && res && !res.ok) return;
          clearTimeout(timer);
          timer = setTimeout(async () => {
            const d = document.querySelector('cart-drawer');
            if (!d || !d.refresh) return;
            while (d._refreshing) await new Promise((r) => setTimeout(r, 50));
            await d.refresh();
            if (isAdd && !d.classList.contains('active')) d.open();
          }, 300);
        }).catch(() => {});
      }
    } catch (e) {}
    return p;
  };
})();
